import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {PersonResponseBodyV2, useConvertToNaturalPersonMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getNaturalPersonFullName, handleApiError} from '@dms/shared';

interface SelectFoundingPersonFormProps {
  customerId: string;
  contactPersons: PersonResponseBodyV2[];
}

export function SelectFoundingPersonForm(props: SelectFoundingPersonFormProps) {
  const [convertToNaturalPerson] = useConvertToNaturalPersonMutation();

  const handleSubmit: FormSubmitHandler<SelectFoundPersonFormSchemaType> = async (data) => {
    await convertToNaturalPerson({
      customerId: props.customerId,
      convertToNaturalPersonRequestBody: {foundingPersonId: data.foundingPersonId},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.success'));
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const contactOptions = props.contactPersons.map((contact) => ({
    label: getNaturalPersonFullName(contact),
    value: contact.id,
  }));

  return (
    <Form<SelectFoundPersonFormSchemaType>
      experimentalZodSchema={SelectFoundPersonFormSchema}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="choice"
            name="foundingPersonId"
            isRequired
            label={i18n.t('entity.customer.actions.selectFoundingPerson')}
            options={contactOptions}
          />

          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={closeCurrentDialog}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.save')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

const SelectFoundPersonFormSchema = z.object({
  foundingPersonId: z.string().min(1),
});

type SelectFoundPersonFormSchemaType = z.infer<typeof SelectFoundPersonFormSchema>;
